import PropTypes from 'prop-types';
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import { forwardRef } from 'react';
import useContentElement from '../../hooks/useContentElement';

function TypographyElement(props, ref) {
  const {
    data = {
      semanticVariant: 'div',
      displayVariant: 'body1',
    },
    className,
    sx,
    ...otherProps
  } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { elementData } = useContentElement(
    data,
    TypographyElementExport.dataSchema,
  );

  // Set default values
  const { displayVariantMobile } = elementData;
  const semanticVariant = elementData.semanticVariant ?? 'div';
  let displayVariant = elementData.displayVariant ?? 'body1';
  let textAlign = elementData.textAlign ?? 'left';
  let htmlValue = elementData.value ?? '';
  /* let htmlValue = elementData.value
    ? marked.parse(findAndReplaceIcons(elementData.value))
    : ''; */

  // ARIA: no justified text
  if (textAlign === 'justify') {
    textAlign = 'left';
  }

  let displayStyle = {};

  if (isMobile && displayVariantMobile) {
    displayVariant = displayVariantMobile;
  }

  if (displayVariant === 'display1') {
    displayStyle = theme.typography.display1;
  } else if (displayVariant === 'display2') {
    displayStyle = theme.typography.display2;
  } else if (displayVariant === 'display3') {
    displayStyle = theme.typography.display3;
  }

  if (semanticVariant.startsWith('h')) {
    htmlValue = htmlValue.replaceAll('<p>', '');
    htmlValue = htmlValue.replaceAll('</p>', '');
  }

  return (
    <Typography
      {...otherProps}
      ref={ref}
      align={textAlign}
      className={className}
      component={semanticVariant}
      dangerouslySetInnerHTML={{ __html: htmlValue }}
      sx={{
        ...sx,
        ...displayStyle,
        '& p + ul': {
          marginTop: theme.spacing(-2),
          marginBottom: theme.spacing(2),
        },
        hyphens: 'auto',
        '& p:not(:last-child)': {
          marginBottom: theme.spacing(2),
        },
        '& a[href]': {
          color: 'inherit',
          textDecoration: 'underline',
          fontWeight: '600',
        },
        '& a[href]:hover, a[href]:focus': {
          color: theme.palette.primary.dark,
        },

      }}
      variant={displayVariant}
    />
  );
}

const TypographyElementMemorized = forwardRef(TypographyElement);
const TypographyElementExport = TypographyElementMemorized;
export default TypographyElementExport;

/* marked.use({
  async: false,
  smartypants: true,
  breaks: true,
}); */

const SEMANTIC_VARIANTS = [
  null,
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'div',
  'span',
];
const DISPLAY_VARIANTS = [
  null,
  'display1',
  'display2',
  'display3',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'subtitle1',
  'subtitle2',
  'body1',
  'body2',
  'caption',
  'button',
  'overline',
  'srOnly',
  'inherit',
];
const TEXT_ALIGNMENTS = [
  null,
  'left',
  'center',
  'right',
  'justify',
];

TypographyElementExport.typeName = 'ComponentElementsTypographyElement'; // Strapi element type
TypographyElementExport.propTypes = {
  data: PropTypes.shape({
    value: PropTypes.string,
    semanticVariant: PropTypes.oneOf(SEMANTIC_VARIANTS),
    displayVariant: PropTypes.oneOf(DISPLAY_VARIANTS),
    displayVariantMobile: PropTypes.oneOf(DISPLAY_VARIANTS),
    textAlign: PropTypes.oneOf(TEXT_ALIGNMENTS),
  }).isRequired,
  color: PropTypes.string,
};

TypographyElementExport.graphQlSchema = `
  id
  value
  semanticVariant
  displayVariant
  displayVariantMobile
  textAlign
`;
